import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgSelectOption,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { map, startWith, Subject, takeUntil } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { MatPseudoCheckboxModule } from '@angular/material/core';
import {
  NgOptgroupTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
  NgSelectModule,
} from '@ng-select/ng-select';
import { MatIcon } from '@angular/material/icon';
import { formatDisplayText, formatValueSafely } from '../../utils/text-case.utils';
import {
  collectDependencyValues,
  dependencySignature,
  resolveDependencyKeys,
} from '../../utils/dropdown-dependency.utils';

@Component({
  selector: 'app-global-dropdown',
  imports: [
    
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatPseudoCheckboxModule,
    NgSelectComponent,
    FormsModule,
    NgOptionTemplateDirective,
    NgSelectModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GlobalDropdownComponent),
      multi: true,
    },
  ],
  templateUrl: './global-dropdown.component.html',
  styleUrl: './global-dropdown.component.scss',
})
export class GlobalDropdownComponent implements ControlValueAccessor, OnChanges, OnInit, AfterViewInit {
  @Input() data: any;
  @Input() options: any[] = [];
  @Output() filterChanged = new EventEmitter<any>();
  private suppressSelectionChange = false;
  private destroy$ = new Subject<void>();
  private populationRequestSignature = '';

  selected: any[] = [];
  searchCtrl = new FormControl('');
  filteredOptions: any[] = [];
  isDropdownOpen = false;
  visiblePlainSelectionCount = 0;

  selectedDropdownList: { [key: string]: any[] } = {};

  private onChangeFn: (value: any) => void = () => {};
  private onTouchedFn: () => void = () => {};
  private resizeObserver?: ResizeObserver;
  private selectionMeasureFrame?: number;

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private elementRef: ElementRef<HTMLElement>,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    // The destination dashboard seeds navigation-carried selections before
    // dynamic dropdowns are created. Seed from the snapshot because filter$
    // is intentionally a Subject and does not replay that earlier emission.
    this.syncSelectionFromFilters(this.globalFilterService.getCurrentFilters?.());

    this.globalFilterService.filter$
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => {
        this.syncSelectionFromFilters(filters);
        // A field with `dependsOn` narrows its options by the other filters,
        // so a change to any of them has to re-request the list. loadOptions
        // no-ops when the dependency snapshot is unchanged, so fields without
        // `dependsOn` never issue a second request.
        if (this.data?.reloadOptionsOnFilterChange !== false) {
          this.loadOptions();
        }
      });

    this.searchCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((searchText: string | null) => {
        const term = (searchText || '').toLowerCase();
        if (!term) {
          this.filteredOptions = [...this.options];
        } else {
          this.filteredOptions = this.options.filter((opt) => opt.toLowerCase().includes(term));
        }
      });

    // Dashboard widgets are created dynamically and receive `data` through a
    // direct instance assignment. That does not trigger ngOnChanges, so load
    // dropdown options here as well.
    this.loadOptions();
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => this.schedulePlainSelectionMeasurement());
    });
    this.resizeObserver.observe(this.elementRef.nativeElement);
    const selectContainer = this.elementRef.nativeElement.querySelector<HTMLElement>('.ng-select-container');
    if (selectContainer) this.resizeObserver.observe(selectContainer);
    this.schedulePlainSelectionMeasurement();
  }

  private syncSelectionFromFilters(filters: any): void {
    const key = this.data?.key;
    if (!key) return;
    const value = this.data?.isQueryParam
      ? filters?.queryParams?.[key]
      : filters?.requestParams?.[key];
    this.selected = Array.isArray(value) ? [...value] : value !== null && value !== undefined && value !== '' ? [value] : [];
    this.schedulePlainSelectionMeasurement();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.loadOptions();
  }

  private loadOptions(): void {
    const apiUrl = String(this.data?.apiUrl || '').trim();
    if (apiUrl) {
      // Current selections of the keys this field depends on. Empty for a
      // field without `dependsOn`, which keeps its signature constant and so
      // preserves the load-once behaviour.
      const dependencyKeys = resolveDependencyKeys(this.data);
      const dependencyValues = collectDependencyValues(
        this.globalFilterService.getCurrentFilters?.(),
        dependencyKeys,
        this.data?.scalarFilterKeys,
      );

      const requestSignature = JSON.stringify({
        apiUrl,
        method: this.data?.method || 'post',
        key: this.data?.responseDataKey || this.data?.key || '',
        skipDefaultPayload: this.data?.skipDefaultPayload === true,
        customPayload: this.data?.customPayload || null,
        dependencies: dependencySignature(dependencyValues),
      });

      // A normally bound input invokes ngOnChanges before ngOnInit. Avoid
      // issuing the same population request twice in that case.
      if (requestSignature === this.populationRequestSignature) {
        return;
      }
      this.populationRequestSignature = requestSignature;

      const requestData = this.buildDependentFilterListRequestData(dependencyKeys, dependencyValues);
      const filters = requestData === this.data && dependencyKeys.length
        ? { requestParams: dependencyValues }
        : undefined;

      this.configService.getConfig(apiUrl, requestData, filters, this.data?.method).subscribe({
        next: (res) => {
          // Ignore an older response when the user changed a dependency while
          // that request was still in flight.
          if (requestSignature !== this.populationRequestSignature) return;

          const responseOptions = this.resolveResponseOptions(res);
          const selectedFieldIsFilteredOut =
            this.data?.dependenciesAsFilterList === true &&
            Object.prototype.hasOwnProperty.call(
              dependencyValues,
              String(this.data?.key || ''),
            );

          // A selected field is intentionally absent from attributesList, so
          // its response has no replacement domain. Retain its existing list
          // to let the user change the selection; only the remaining dropdowns
          // consume the dependent response.
          if (!selectedFieldIsFilteredOut) {
            this.options = responseOptions;
          }
          this.filteredOptions = this.options;
          this.data.optionsList = this.options;
          this.schedulePlainSelectionMeasurement();
        },
        error: (err) => console.error(err),
      });
    } else {
      this.filteredOptions = this.options || [];
    }
  }

  /**
   * Some getData endpoints accept dependent selections as filterList rules
   * rather than requestData properties. This opt-in keeps the configured
   * payload unchanged for the initial request. On later requests it preserves
   * mandatory rules, moves each selected dropdown from attributesList to one
   * filterList rule. A lone remaining attribute uses `distinct <key>`.
   * With multiple attributes, configured names stay plain except customer_no,
   * which always uses `distinct customer_no`. Rules configured in
   * dependentSelectionFilterList are appended only after a selection exists.
   * Single selections use `=` with a scalar value; multi-selections use `in`
   * with one array. Every filter except the final rule is joined with AND.
   * Passing no filters to ConfigService is intentional: requestData must remain
   * exactly as configured for this contract.
   */
  private buildDependentFilterListRequestData(
    dependencyKeys: string[],
    dependencyValues: Record<string, unknown>,
  ): any {
    if (this.data?.dependenciesAsFilterList !== true) {
      return this.data;
    }

    const selectedValues = new Map<string, unknown[]>();
    dependencyKeys.forEach((key) => {
      const rawValue = dependencyValues[key];
      const values = (Array.isArray(rawValue) ? rawValue : [rawValue])
        .flat(Infinity)
        .filter((value) => value !== null && value !== undefined && value !== '');
      if (values.length) selectedValues.set(key, values);
    });

    if (!selectedValues.size) {
      return this.data;
    }

    const dependencyKeySet = new Set(dependencyKeys);
    const selectedKeySet = new Set(selectedValues.keys());
    const normalizeAttributeName = (attribute: unknown): string =>
      String(attribute ?? '').trim().replace(/^distinct\s+/i, '').trim();

    const configuredPayload = this.data?.customPayload || {};
    const configuredAttributes = Array.isArray(configuredPayload.attributesList)
      ? configuredPayload.attributesList
      : [];
    const remainingAttributes = configuredAttributes.flatMap((attribute: unknown) => {
      const key = normalizeAttributeName(attribute);
      if (!key || selectedKeySet.has(key)) return [];
      return [key];
    });
    const attributesList = remainingAttributes.map((attribute: string) =>
      remainingAttributes.length === 1 || attribute === 'customer_no'
        ? `distinct ${attribute}`
        : attribute,
    );

    const configuredFilters = Array.isArray(configuredPayload.filterList)
      ? configuredPayload.filterList
      : [];
    const selectionOnlyFilters = Array.isArray(this.data?.dependentSelectionFilterList)
      ? this.data.dependentSelectionFilterList
      : [];
    const mandatoryFilters = [...configuredFilters, ...selectionOnlyFilters]
      .filter(
        (rule: any) => !dependencyKeySet.has(String(rule?.attributeName ?? '').trim()),
      );
    const selectedRules = dependencyKeys.flatMap((key) => {
      const values = selectedValues.get(key);
      return values
        ? [{
            attributeName: key,
            operator: values.length > 1 ? 'in' : '=',
            logicalOperator: '',
            beginScope: '',
            endScope: '',
            attributeValue: values.length === 1 ? values[0] : [...values],
          }]
        : [];
    });
    const filterList = [...mandatoryFilters, ...selectedRules].map(
      (rule: any, index: number, rules: any[]) => ({
        ...rule,
        logicalOperator: index < rules.length - 1 ? 'AND' : '',
      }),
    );

    return {
      ...this.data,
      customPayload: {
        ...configuredPayload,
        attributesList,
        filterList,
      },
    };
  }

  private resolveResponseOptions(response: any): any[] {
    const responseKey = String(this.data?.responseDataKey || this.data?.key || '').trim();
    const configuredRows = this.resolveResponsePath(response, this.data?.responseRowsPath);
    const entityKey = String(this.data?.customPayload?.requestData?.entity || '').trim();
    const entityRows = entityKey
      ? [
          this.resolveResponsePath(response, `responseList.0.${entityKey}`),
          this.resolveResponsePath(response, `responseList.0.data.0.${entityKey}`),
          this.resolveResponsePath(response, `data.${entityKey}`),
          response?.[entityKey],
        ].find(Array.isArray)
      : undefined;
    const directResponseValue = responseKey ? response?.[responseKey] : undefined;
    const source = Array.isArray(configuredRows)
      ? configuredRows
      : Array.isArray(entityRows)
        ? entityRows
      : Array.isArray(response)
        ? response
      : Array.isArray(response?.data)
        ? response.data
      : directResponseValue !== undefined
        ? Array.isArray(directResponseValue) ? directResponseValue : [directResponseValue]
      : responseKey && response && typeof response === 'object'
        ? [response]
        : [];

    if (!Array.isArray(source)) {
      return [];
    }

    // Platform filter responses may be primitive arrays or getData row
    // objects. For rows, extract the field configured by responseDataKey.
    const options = source
      .flat(Infinity)
      .flatMap((value) =>
        value && typeof value === 'object' && Array.isArray(value.data)
          ? value.data
          : [value],
      )
      .map((value) =>
        responseKey && value && typeof value === 'object' && !Array.isArray(value)
          ? value[responseKey]
          : value,
      )
      .flatMap((value) => typeof value === 'string' ? value.split(',') : [value])
      .filter((value) => value !== null && value !== undefined)
      .map((value) => String(value).trim())
      .filter(Boolean);

    const uniqueOptions = [...new Set(options)];
    const sortDirection = String(this.data?.sortOptions || '').trim().toLowerCase();
    if (sortDirection === 'asc' || sortDirection === 'desc') {
      const direction = sortDirection === 'asc' ? 1 : -1;
      uniqueOptions.sort((left, right) =>
        direction * String(left).localeCompare(String(right), undefined, {
          numeric: true,
          sensitivity: 'base',
        }),
      );
    }

    return uniqueOptions;
  }

  private resolveResponsePath(source: any, path: unknown): any {
    const normalizedPath = String(path || '').trim();
    if (!normalizedPath) return undefined;
    return normalizedPath
      .split('.')
      .reduce((current: any, part) => current?.[part], source);
  }

  get isAllSelected(): boolean {
    return this.options?.length > 0 && this.selected?.length === this.options.length;
  }

  get placeholderText(): string {
    return this.data?.disabled ? (this.data?.disabledPlaceholder || 'Select a value first') : 'All';
  }

  /**
   * Where ng-select renders its dropdown panel. Empty (the default) keeps the
   * panel inline, where it is subject to every ancestor's `overflow` and
   * stacking context - ng-select only relocates the panel when `appendTo` is
   * truthy, so '' preserves today's behaviour for every existing widget. A
   * widget packed into a tight layout - stacked rows, a card with
   * `overflow: hidden` - sets `"appendTo": "body"` so the panel escapes to a
   * body-level overlay and can neither be clipped nor painted over by
   * whatever follows it.
   */
  get appendTarget(): string {
    return String(this.data?.appendTo || '').trim();
  }

  isItemSelected(item: any): boolean {
    return this.selected?.includes(item);
  }

  toggleSelectAll(event: any): void {
    if (this.data?.disabled) {
      return;
    }
    const isChecked = event?.target?.checked;

    // Prevent triggering onChange again
    this.suppressSelectionChange = true;
    // this.selected = isChecked ? [...this.filteredOptions] : [];
    this.selected = isChecked ? [...this.options] : [];

    // Emit manually once
    this.onChangeFn(this.selected);
    this.onTouchedFn();

    const valueToEmit = this.data?.isMultiSelect ? this.selected : this.selected;

    if (this.data?.key) {
      this.globalFilterService.activeFilter = this.data.key;
      // Pass filterListener if available
      const filterListener = this.data?.filterListener || null;
      this.globalFilterService.updateFilterSelection(
        this.data.key,
        valueToEmit,
        this.data?.isQueryParam,
        undefined,
        true,
        filterListener,
      );
    }

    this.filterChanged.emit(valueToEmit);
    this.schedulePlainSelectionMeasurement();

    setTimeout(() => {
      this.suppressSelectionChange = false;
    }, 0);
  }

  onChange(event: any): void {
    if (this.data?.disabled) return;
    if (this.suppressSelectionChange) return;
    // ng-select supplies the new model through ngModelChange. Use that value
    // directly instead of relying on two-way binding listener order; otherwise
    // the UI can show the new choice while the global payload retains the
    // previous selection.
    this.selected = Array.isArray(event)
      ? [...event]
      : event === null || event === undefined || event === ''
        ? []
        : [event];
    this.onChangeFn(this.selected);
    this.onTouchedFn();

    const valueToEmit = this.data?.isMultiSelect ? this.selected : this.selected;

    if (this.data?.key) {
      this.globalFilterService.activeFilter = this.data?.key;
      // Pass filterListener if available in data.filterListener or parent config
      const filterListener = this.data?.filterListener || null;
      this.globalFilterService.updateFilterSelection(
        this.data?.key,
        valueToEmit,
        this.data?.isQueryParam,
        undefined,
        true,
        filterListener,
      );
    }

    this.filterChanged.emit(valueToEmit);
    this.schedulePlainSelectionMeasurement();
  }

  onItemClick(item: any): void {
    if (this.data?.disabled) {
      return;
    }
    if (!this.data?.isMultiSelect) {
      if (this.selected === item) {
        // Unselect if same item clicked again
        this.selected = [];
        this.onChangeFn(this.selected);
        this.onTouchedFn();

        const valueToEmit = this.selected;

        if (this.data?.key) {
          this.globalFilterService.activeFilter = this.data?.key;
          // Pass filterListener if available
          const filterListener = this.data?.filterListener || null;
          this.globalFilterService.updateFilterSelection(
            this.data.key,
            valueToEmit,
            this.data?.isQueryParam,
            undefined,
            true,
            filterListener,
          );
        }

        this.filterChanged.emit(valueToEmit);
        this.schedulePlainSelectionMeasurement();
      }
    }
  }

  removeSelected(item: any): void {
    this.selected = this.selected.filter((i) => i !== item);
    this.onChange(this.selected); // emit the change
  }

  onDropdownClose() {
    this.isDropdownOpen = false;
    this.searchCtrl.setValue('', { emitEvent: true }); // clears input
  }

  onDropdownOpen(): void {
    this.isDropdownOpen = true;
  }

  writeValue(value: any): void {
    // this.selected = value ?? (this.data?.isMultiSelect ? [] : '');
    this.selected = value || [];
    this.schedulePlainSelectionMeasurement();
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  /**
   * Display-formatting helpers for template.
   * Per UI Standards (Point 3): filter/dropdown names and values in Capital Case.
   * The textCase can be overridden per-widget via `data.textCase` config.
   */
  formatLabel(value: unknown): string {
    const mode = (this.data?.textCase || 'capital') as any;
    return formatDisplayText(value, mode);
  }

  formatOption(value: unknown): string {
    const mode = (this.data?.textCase || 'capital') as any;
    return formatValueSafely(value, mode);
  }

  /**
   * Opt-in compact rendering of a multi-select's values. Runtime measurement
   * determines how many comma-separated labels fit before the "+N more"
   * summary, clear control, and arrow.
   */
  get usePlainTextLabel(): boolean {
    return this.data?.plainTextLabel === true;
  }

  /** Every selected value for the compact field's native title tooltip. */
  plainSelectionText(items: readonly any[] | null | undefined): string {
    return (items ?? [])
      .map((item) => this.formatOption(item?.value !== undefined ? item.value : item))
      .filter((text) => text !== '')
      .join(', ');
  }

  /** Selected values which fit before the dynamic `+N more` summary. */
  visiblePlainSelectionText(items: readonly any[] | null | undefined): string {
    return this.plainSelectionText((items ?? []).slice(0, this.visiblePlainSelectionCount));
  }

  hiddenPlainSelectionCount(items: readonly any[] | null | undefined): number {
    return Math.max(0, (items?.length ?? 0) - this.visiblePlainSelectionCount);
  }

  private schedulePlainSelectionMeasurement(): void {
    if (!this.usePlainTextLabel || typeof requestAnimationFrame === 'undefined') return;
    if (this.selectionMeasureFrame !== undefined) {
      cancelAnimationFrame(this.selectionMeasureFrame);
    }
    this.selectionMeasureFrame = requestAnimationFrame(() => {
      this.selectionMeasureFrame = undefined;
      this.measurePlainSelection();
    });
  }

  private measurePlainSelection(): void {
    const host = this.elementRef.nativeElement;
    const label = host.querySelector<HTMLElement>('.plain-multi-label');
    const valueContainer = host.querySelector<HTMLElement>('.ng-value-container');
    if (!label || !valueContainer) return;

    const valueRect = valueContainer.getBoundingClientRect();
    const controlBoundary = this.resolveSelectionControlBoundary(valueRect.right);
    const availableWidth = Math.max(0, controlBoundary - valueRect.left - 4);
    label.style.setProperty('--dvm-plain-selection-available-width', `${availableWidth}px`);

    const labels = (this.selected ?? [])
      .map((item) => this.formatOption(item?.value !== undefined ? item.value : item))
      .filter((text) => text !== '');
    const measurer = this.createTextMeasurer(label);
    try {
      const nextCount = this.fitVisibleSelectionCount(labels, availableWidth, measurer.measure);
      if (nextCount !== this.visiblePlainSelectionCount) {
        this.visiblePlainSelectionCount = nextCount;
      }
    } finally {
      measurer.dispose();
    }
  }

  /** Left edge of the first visible clear/arrow control inside this field. */
  private resolveSelectionControlBoundary(fallbackRight: number): number {
    const host = this.elementRef.nativeElement;
    const field = host.parentElement;
    const controls = [
      host.querySelector<HTMLElement>('.ng-clear-wrapper'),
      host.querySelector<HTMLElement>('.ng-arrow-wrapper'),
      field?.querySelector<HTMLElement>('.ldg-caret') ?? null,
    ];

    return controls.reduce((boundary, control) => {
      if (!control || getComputedStyle(control).display === 'none') return boundary;
      const rect = control.getBoundingClientRect();
      return rect.width > 0 ? Math.min(boundary, rect.left) : boundary;
    }, fallbackRight);
  }

  private createTextMeasurer(source: HTMLElement): {
    measure: (text: string) => number;
    dispose: () => void;
  } {
    const measuringSpan = document.createElement('span');
    const sourceStyle = getComputedStyle(source);
    measuringSpan.style.position = 'fixed';
    measuringSpan.style.visibility = 'hidden';
    measuringSpan.style.pointerEvents = 'none';
    measuringSpan.style.whiteSpace = 'nowrap';
    measuringSpan.style.font = sourceStyle.font;
    measuringSpan.style.letterSpacing = sourceStyle.letterSpacing;
    document.body.appendChild(measuringSpan);

    return {
      measure: (text: string): number => {
        measuringSpan.textContent = text;
        return measuringSpan.getBoundingClientRect().width;
      },
      dispose: () => measuringSpan.remove(),
    };
  }

  private fitVisibleSelectionCount(
    labels: string[],
    availableWidth: number,
    measure: (text: string) => number,
  ): number {
    for (let visible = labels.length; visible >= 0; visible -= 1) {
      const valueText = labels.slice(0, visible).join(', ');
      const remaining = labels.length - visible;
      const summaryText = remaining > 0 ? `+${remaining} more` : '';
      const gapWidth = valueText && summaryText ? 4 : 0;
      if (measure(valueText) + gapWidth + measure(summaryText) <= availableWidth) {
        return visible;
      }
    }
    return 0;
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.selectionMeasureFrame !== undefined) {
      cancelAnimationFrame(this.selectionMeasureFrame);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
